import type { AutoSpinStore } from "../../autospin/AutoSpinStore";
import type { FeatureStateStore } from "../../feature-state/FeatureStateStore";
import type { FeatureModeStore } from "../../feature-mode/FeatureModeStore";
import type { SpinButtonPort } from "./SpinButtonPort";
import type { SpinCoordinator } from "../../spin/SpinCoordinator";
import type { SpinInputPresenter } from "./SpinInputPresenter";

export class SpinButtonPresenter {
  private readonly panel: SpinButtonPort;
  private readonly autoSpinStore: AutoSpinStore;
  private readonly featureStateStore: FeatureStateStore;
  private readonly featureModeStore: FeatureModeStore;
  private readonly spinCoordinator: SpinCoordinator;
  private readonly spinInputPresenter: SpinInputPresenter | null;
  /** Whether the viewport is a mobile shape, asked at every render (the bar presenter re-renders on a viewport change). */
  private readonly isMobileLayout: () => boolean;
  /** A round waiting for the player's Spin (decision 55). */
  private readonly playerSpins: { readonly waiting: boolean } | null;
  /** The feature HUD stands on the button's spot on the mobile shapes (a title without it keeps the button in a feature). */
  private readonly featureHudInBar: boolean;

  private wasSpinAvailable = false;

  public constructor(input: {
    panel: SpinButtonPort;
    autoSpinStore: AutoSpinStore;
    featureStateStore: FeatureStateStore;
    featureModeStore: FeatureModeStore;
    spinCoordinator: SpinCoordinator;
    spinInputPresenter?: SpinInputPresenter | null;
    /** True on a mobile shape, portrait or landscape: there a feature hides the button and its slam face. Default: never. */
    isMobileLayout?: () => boolean;
    /** A round waiting for the player's Spin (decision 55): the spin face, pressable, whatever else holds. */
    playerSpins?: { readonly waiting: boolean } | null;
    /** The bar shows the feature HUD (default): on a mobile shape it takes the button's spot in a feature. */
    featureHudInBar?: boolean;
  }) {
    this.isMobileLayout = input.isMobileLayout ?? (() => false);
    this.playerSpins = input.playerSpins ?? null;
    this.featureHudInBar = input.featureHudInBar ?? true;
    this.panel = input.panel;
    this.autoSpinStore = input.autoSpinStore;
    this.featureStateStore = input.featureStateStore;
    this.featureModeStore = input.featureModeStore;
    this.spinCoordinator = input.spinCoordinator;
    this.spinInputPresenter = input.spinInputPresenter ?? null;
  }

  public render(): void {
    const snap = this.spinCoordinator.getSnapshot();
    const autoSpinRunning = this.autoSpinStore.getSnapshot().running;
    const inFeatureGame = this.featureStateStore.isFeatureActive();
    const isMobile = this.isMobileLayout();
    const hiddenForFeatureMobile = inFeatureGame && isMobile && this.featureHudInBar;
    const featureModeActive = !!this.featureModeStore.getSnapshot().activeMode;
    // A round waiting for the player's Spin shows the spin face, even where a feature would hide the button: a wait
    // nobody can press would hold the round for good.
    const waitingForPlayer = this.playerSpins?.waiting ?? false;

    this.panel.setFeatureModeStyle(featureModeActive);

    this.panel.setVisible(!autoSpinRunning && (waitingForPlayer || !hiddenForFeatureMobile));

    const slamVisible = snap.canSlamStop && !hiddenForFeatureMobile && !waitingForPlayer;
    this.panel.setSlamVisible(slamVisible);

    let spinAvailable = false;

    if (waitingForPlayer) {
      // Pressable, but no spin is available: the hold and the idle hint stay off.
      this.panel.setLocked(false);
      this.panel.setEnabled(true);
    } else if (slamVisible) {
      this.panel.setLocked(false);
      this.panel.setEnabled(true);
    } else {
      spinAvailable = snap.canSpin;
      this.panel.setLocked(!spinAvailable);
      this.panel.setEnabled(spinAvailable);
    }

    if (!this.wasSpinAvailable && spinAvailable) {
      this.spinInputPresenter?.onSpinBecameAvailable();
    } else if (this.wasSpinAvailable && !spinAvailable) {
      this.spinInputPresenter?.onSpinBecameUnavailable();
    }

    this.wasSpinAvailable = spinAvailable;

    if (slamVisible || !spinAvailable) {
      this.panel.setHoldHintVisible(false);
      this.panel.resetHoldWarmupVisualImmediate();
    }
  }
}
